/**
 * BudgetConfigForm — write-slug-gated budget ceiling editor for the AI
 * Consumption Console (P3). Behind `ops.app_config.write` (canWrite): when
 * false, renders a read-only notice; when true, an RHF + Zod form pre-filled
 * from `budget.ceilings` that persists via `useSaveBudget` (five app_config
 * writes) with success/error toasts.
 *
 * NOTE (deviation from plan Task 11): the plan wrapped each `<input>` as a
 * *child* of `<Field>`, but the @garden/ui `Field` primitive renders its own
 * `<input>` from `{...rest}` and ignores children — so children never mount and
 * the fields would not bind to RHF. Every existing admin form (MarketFormDialog,
 * CategoryFormDialog, SellerEditForm, ProductEditForm, InviteAdminDialog)
 * spreads `{...register(...)}` directly onto `<Field>`; this file follows that
 * established, correct pattern.
 */
import { type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { Button, Field } from "@garden/ui";
import { ADMIN_NS } from "@/features/admin/api";
import { useSaveBudget, type BudgetFormValues } from "@/features/admin/ai-usage/hooks";
import type { BudgetStatusResponse } from "@/features/admin/ai-usage/api";

const numStr = z.string().refine((v) => v.trim() !== "" && Number.isFinite(Number(v)) && Number(v) >= 0, "invalid");
const schema = z.object({
  dailyUsd: numStr, monthlyUsd: numStr, dailyTokens: numStr, monthlyTokens: numStr,
  alertPct: numStr.refine((v) => Number(v) >= 0 && Number(v) <= 100, "pct"),
});

export function BudgetConfigForm({ budget, canWrite }: { budget: BudgetStatusResponse; canWrite: boolean }): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const save = useSaveBudget();
  const { register, handleSubmit, formState: { errors } } = useForm<BudgetFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      dailyUsd: String(budget.ceilings.daily_ceiling_usd),
      monthlyUsd: String(budget.ceilings.monthly_ceiling_usd),
      dailyTokens: String(budget.ceilings.daily_ceiling_tokens),
      monthlyTokens: String(budget.ceilings.monthly_ceiling_tokens),
      alertPct: String(budget.alert_threshold_pct),
    },
  });

  if (!canWrite) {
    return <p data-testid="ai-budget-readonly" className="text-sm text-[var(--g-secondary)]">{t("aiUsage.budget.readOnly")}</p>;
  }

  const onSubmit = handleSubmit((values) => {
    save.mutate(values, {
      onSuccess: () => toast.success(t("aiUsage.budget.saved")),
      onError: () => toast.error(t("aiUsage.budget.saveError")),
    });
  });

  return (
    <form onSubmit={onSubmit} data-testid="ai-budget-form" className="flex flex-col gap-4">
      <Field label={t("aiUsage.budget.dailyUsd")} inputMode="decimal" error={errors.dailyUsd ? t("aiUsage.budget.invalid") : undefined} {...register("dailyUsd")} />
      <Field label={t("aiUsage.budget.monthlyUsd")} inputMode="decimal" error={errors.monthlyUsd ? t("aiUsage.budget.invalid") : undefined} {...register("monthlyUsd")} />
      <Field label={t("aiUsage.budget.dailyTokens")} inputMode="numeric" error={errors.dailyTokens ? t("aiUsage.budget.invalid") : undefined} {...register("dailyTokens")} />
      <Field label={t("aiUsage.budget.monthlyTokens")} inputMode="numeric" error={errors.monthlyTokens ? t("aiUsage.budget.invalid") : undefined} {...register("monthlyTokens")} />
      <Field label={t("aiUsage.budget.alertPct")} inputMode="numeric" error={errors.alertPct ? t("aiUsage.budget.invalid") : undefined} {...register("alertPct")} />
      <div>
        <Button type="submit" disabled={save.isPending}>{t("aiUsage.budget.save")}</Button>
      </div>
    </form>
  );
}
export default BudgetConfigForm;
